// Shared formatter for a logged set/session's displayed amount, used
// everywhere a log entry's weight/reps (or, for a cardio exercise,
// duration/distance) is rendered as text -- ExerciseHistoryModal,
// WorkoutLog.jsx's "Previously..." list and Quick Start recap, and
// ActiveMission's "Previous" column and debrief. A single shared helper
// instead of re-deriving this branch at each call site, so the two log
// shapes (strength: weight/reps; cardio: durationMinutes/distance) always
// render identically regardless of which screen is showing them.
//
// A log is "cardio" purely by the presence of `durationMinutes` -- strength
// logs never carry that field, cardio logs always do (duration is
// required, see ActiveMission/WorkoutLog's cardio logging validation),
// so this is a reliable discriminator without needing the exercise doc
// itself in hand at render time.
export const isCardioLog = (log) =>
  log != null && log.durationMinutes !== undefined && log.durationMinutes !== null && log.durationMinutes !== "";

// durationMinutes/distance are accepted as either a number (the real
// Firestore shape) or a numeric string (an in-progress row's raw input
// value, before it's been committed) -- Number(...) normalizes either.
export const formatCardioAmount = (durationMinutes, distance) => {
  const totalMinutes = Math.max(0, Math.round(Number(durationMinutes) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const durationText = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}` : `${minutes} min`;
  const distanceNum = distance !== "" && distance !== undefined && distance !== null ? Number(distance) : null;
  return distanceNum ? `${durationText} · ${distanceNum} mi` : durationText;
};

export const formatLogAmount = (log) => {
  if (isCardioLog(log)) return formatCardioAmount(log.durationMinutes, log.distance);
  return `${log.weight} lb x ${log.reps} reps`;
};

// Compact "H:MM, Dist=D" clock-style format. Originally built just for
// ActiveMission's "Previous" column (a tight, fixed-width space that needed
// squeezing -- no "mi" unit suffix, no period after "Dist", no spaces
// around "=", all per direct user request), then extended to WorkoutLog.
// jsx's "Previously..." list too, per a later direct request to match that
// column's wording exactly. Quick Start's recap, the debrief, and the "i"
// history popup still use formatCardioAmount's "1h 15m · 3.1 mi" wording
// below -- deliberately untouched, since neither of those two requests
// named them.
export const formatCardioDurationShort = (durationMinutes, distance) => {
  const totalMinutes = Math.max(0, Math.round(Number(durationMinutes) || 0));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const clock = `${hours}:${String(minutes).padStart(2, "0")}`;
  const distanceNum = distance !== "" && distance !== undefined && distance !== null ? Number(distance) : null;
  return distanceNum ? `${clock}, Dist=${distanceNum}` : clock;
};
